import React, { useRef, useState } from 'react';

import './SsnField.css';
import { wrapTrademarkSuperscript } from '../../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { InputSocialSecurity } from 'component-library/index';
import { TBlurEvent, TFocusEvent } from '../../../utils/general-types';
import { generateFieldErrorMessage } from '../../../utils/error-message';
import { useWhitelistContext } from '../../../providers/WhitelistProvider/WhitelistProvider';

export interface ISsnFieldProps {
    name?: string;
    id?: string;
	className?: string;
	value: string;
    setValueCallback: THandlerFunction;
	lastFour?: boolean;
    ssnMessage?: string;
    disclaimerMessage?: string;
    label?: string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactElement<any, string | React.JSXElementConstructor<any>>[];
    helperText?: string;
    inputClass?: string;
    containerClass?:string;
    error?: boolean;
    onFocusCallback?: TFocusEvent;
    onBlurCallback?: TBlurEvent;
    disabled?: boolean;
    ref?: React.MutableRefObject<any>;
    hideAlertIcon?: boolean;
    iconContainerClass?: string;
    valid?: [boolean, (arg?: any) => void];
    // DPM-7649 revisit to fix styling differences between PersonalInformation.tsx and PersonalInformationForm.tsx
    // and prefer a single method/strategy for green checkmarks.
    showDisclaimer?: boolean;
    showSuccessIcon?: boolean;
    removeAsterisk?: boolean;
    required?: boolean;
    advancedMasking?: boolean;
}

// Add prop to show disclaimer on focus (use for PQDE and Application)
export default function SsnField(props: ISsnFieldProps & React.HTMLAttributes<HTMLInputElement>) {
    const { isSsnWhitelisted } = useWhitelistContext();

    const [hasBeenFocused, setHasBeenFocused] = useState(false);
    const [showDesc, setShowDesc] = useState(false);
    const ssnBaseRef = useRef(null);
    
    const validateZeroesSsn = (ssn: string): boolean => {
        if (isSsnWhitelisted(ssn)) {
            return true;
        }

        const firstThree = ssn?.slice(0, 3);
        const middleTwo = ssn?.slice(3, 5);
        const lastFour = ssn?.slice(5, 10);

        if ((firstThree === '000' || middleTwo === '00' || lastFour === '0000')) {
            return false;
        }

        return true;
    };

    return (
        <div 
            className='SsnField__wrapper'
            onBlur={(e) => {
                const currentTarget = e.currentTarget;
                const relatedTarget = e.relatedTarget;

                if (!currentTarget.contains(relatedTarget as Node)) {
                    setShowDesc(false);
                }
            }}
        >
            <InputSocialSecurity
                inputClass={props.inputClass}
                ref={ssnBaseRef}
                name={props.name}
                id={props.id}
                required={props.required}
                label={props.label}
                helperText={props.helperText}
                value={props.value}
                setValue={props.setValueCallback}
                onFocus={(e) => {
                    props.onFocusCallback && props.onFocusCallback(e);

                    if (!hasBeenFocused) {
                        setHasBeenFocused(true);
                    }   
                    setShowDesc(true);             
                }}
                onBlur={(e: any) => {
                    props.onBlurCallback && props.onBlurCallback(e);
                    const isValid = validateZeroesSsn(props.value) && props.value.length === 9;
                    props.valid[1](isValid);
                }}
                onInput={(e: any) => {
                    if (e.data && e.data.length > 1) {
                        props.setValueCallback('');
                    }

                    const isValid = validateZeroesSsn(props.value);
                    if (!isValid) {
                        props.setValueCallback('');
                    }
                    props.valid[1](isValid);
                }}
                onPaste={(e: any) => {
                    e.preventDefault();
                }}
                valid={props.valid}
                error={props.error}
                errorText={generateFieldErrorMessage('Social Security Number', 'missingValue')}
                invalidErrorMessage={generateFieldErrorMessage('Social Security Number')}
                requiredMessage={generateFieldErrorMessage('Social Security Number', 'missingValue')}
                showSuccessIcon={props.showSuccessIcon !== undefined ? props.showSuccessIcon : hasBeenFocused && props.valid[0]}
                removeAsterisk={props.removeAsterisk}
                useSuccessIcon
                advancedMasking={props.advancedMasking}
                showExclamationError
                alternate
            />
            {props.disclaimerMessage && showDesc && (
                <p tabIndex={-1}
                    className='SsnField__disclaimer'
                >
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(props.disclaimerMessage))}
                </p>
            )}
        </div>
    );
}